import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { useState, useRef } from "react"
import { FileText, X, Upload } from "lucide-react"
import { toast } from "sonner"
import { FileCategory } from "@/modules/shared/hooks/use-file-validation"
import {
  DraggableFileUpload,
  DraggableFileUploadRef,
} from "@/modules/shared/ui/components/custom/file-upload"

interface UploadCvDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (files: File[]) => void
  applicantName?: string
}

export function UploadCvDialog({
  open,
  onOpenChange,
  onConfirm,
  applicantName: _applicantName = "Applicant",
}: UploadCvDialogProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const fileUploadRef = useRef<DraggableFileUploadRef>(null)

  const handleConfirm = () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file before continuing.")
      return
    }
    onConfirm(selectedFiles)
    setSelectedFiles([])
    fileUploadRef.current?.clearError()
  }

  const handleClose = () => {
    setSelectedFiles([])
    fileUploadRef.current?.clearError()
    onOpenChange(false)
  }

  const handleFileSelected = (file: File) => {
    setSelectedFiles((prev) => {
      if (prev.some((f) => f.name === file.name && f.size === file.size)) return prev
      return [...prev, file]
    })
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const getFileName = (file: File) =>
    file.name.length > 35 ? file.name.substring(0, 32) + "..." : file.name

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px] bg-card">
        <DialogHeader>
          <DialogTitle>Upload Candidate CV(s)</DialogTitle>
          <DialogDescription className="text-muted-foreground mt-2">
            Upload one or more CVs to add applicants. Accepted formats: PDF, DOC, DOCX (Max 10MB each)
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <DraggableFileUpload
            ref={fileUploadRef}
            fileCategory={FileCategory.Document}
            onFileSelected={handleFileSelected}
          />

          {selectedFiles.length > 0 && (
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {selectedFiles.map((file, i) => (
                <li key={i} className="flex items-center justify-between border border-border rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-5 w-5 text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{getFileName(file)}</p>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeFile(i)}
                    className="text-destructive/60 hover:text-destructive shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedFiles.length === 0}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload {selectedFiles.length > 1 ? `${selectedFiles.length} CVs` : "CV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
